import { useRef, useState, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { Spinner } from '../Spinner/Spinner';
import styles from './FileUploader.module.css';

const DEFAULT_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
const DEFAULT_TARGET_MB = 2;

interface FileUploaderProps {
  value?: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  accept?: string[];
  maxSizeBytes?: number;
  targetSizeMb?: number;
  error?: { message?: string };
  label?: string;
  disabled?: boolean;
}

export function FileUploader({
  value = [],
  onChange,
  maxFiles = 7,
  accept = DEFAULT_ACCEPT,
  maxSizeBytes = DEFAULT_MAX_BYTES,
  targetSizeMb = DEFAULT_TARGET_MB,
  error,
  label,
  disabled,
}: FileUploaderProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>(() =>
    value.map((f) => URL.createObjectURL(f)),
  );
  const [compressing, setCompressing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || disabled) return;
      setValidationError(null);

      const incoming = Array.from(fileList);
      const rejected: string[] = [];
      const valid: File[] = [];

      for (const f of incoming) {
        if (!accept.includes(f.type)) {
          rejected.push(`${f.name}: unsupported type`);
          continue;
        }
        if (f.size > maxSizeBytes) {
          rejected.push(`${f.name}: exceeds ${Math.round(maxSizeBytes / (1024 * 1024))} MB limit`);
          continue;
        }
        valid.push(f);
      }

      if (rejected.length) {
        setValidationError(rejected[0] ?? null);
      }

      const remaining = maxFiles - value.length;
      const toProcess = valid.slice(0, remaining);
      if (!toProcess.length) return;

      setCompressing(true);
      try {
        const compressed = await Promise.all(
          toProcess.map((f) =>
            imageCompression(f, {
              maxSizeMB: targetSizeMb,
              maxWidthOrHeight: 2048,
              useWebWorker: true,
            }),
          ),
        );
        const newPreviews = compressed.map((f) => URL.createObjectURL(f));
        setPreviews((prev) => [...prev, ...newPreviews]);
        onChange([...value, ...compressed]);
      } finally {
        setCompressing(false);
        // Reset the input so re-selecting the same file fires onChange again.
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [accept, disabled, maxFiles, maxSizeBytes, onChange, targetSizeMb, value],
  );

  function remove(idx: number) {
    const url = previews[idx];
    if (url) URL.revokeObjectURL(url);
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
    onChange(value.filter((_, i) => i !== idx));
  }

  const canAdd = value.length < maxFiles && !compressing && !disabled;
  const acceptAttr = accept.join(',');

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      <div className={styles.grid}>
        {previews.map((src, i) => (
          <div key={i} className={styles.thumb}>
            <img src={src} alt={`Photo ${i + 1}`} className={styles.thumbImg} />
            {!disabled && (
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => remove(i)}
                aria-label={`Remove photo ${i + 1}`}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}

        {canAdd && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => inputRef.current?.click()}
            aria-label="Add photo"
          >
            {compressing ? <Spinner size="sm" /> : <Upload size={18} />}
            <span>{compressing ? 'Processing…' : 'Add photo'}</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptAttr}
        className={styles.hiddenInput}
        onChange={(e) => void handleFiles(e.target.files)}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />

      {(error?.message ?? validationError) && (
        <p className={styles.errorMsg} role="alert">
          {error?.message ?? validationError}
        </p>
      )}

      <p className={styles.hint}>
        JPEG, PNG or WebP · max {Math.round(maxSizeBytes / (1024 * 1024))} MB per file · up to{' '}
        {maxFiles} photos
      </p>
    </div>
  );
}
